import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { matchPath } from "react-router";
import { useLocation } from "react-router-dom";
import { Loader } from "@progress/kendo-react-indicators";
import { Input } from "@progress/kendo-react-inputs";
import { Popup } from "@progress/kendo-react-popup";
import _ from "lodash";
import { setStateContentManagerData } from "../../actions/contentManager.js";
import { setContextSearchText } from "../../actions/projects.js";
import { icon, number, quote, route } from "../../config";
import { errorCode, label, tooltip } from "../../config/constants.js";
import { editorEntity } from "../../editor/editor.Constant.js";
import { getActiveEditorView } from "../../editor/editorRegistry.js";
import { useKeyPress } from "../../helper/commonHooks";
import { useCloseOnClickOutsideLocal } from "../../utils/common";
import { getFolderPath } from "../ContentManagementSystem/helper.js";
import { ORGANIZATION_FEATURES_TITLES, FEATURE_DISPLAY_NAMES } from "../Admin/ManageOrganization/OrganizationFeatures/organizationFeatures.constants";
import OverallSearchItems from "./OverallSearchItems";
import RecentSearch from "./RecentSearch";
import { getSearchResult } from "./overallsearch.service";
import {
  checkForWhitespace,
  getSearchButtonText,
  handleArrowDown,
  handleArrowUp,
  resolveCmsFileIdFromLocation,
  SIMPLE_SEARCH_PATHS,
  SETTING_TABS,
  ADMIN_ORG_FEATURES,
} from "./search.helper.js";
import { useFindInDocument } from "../../editor/useFindInDocument.js";

/**
 * OverallSearchPopup component for handling global search functionality and displaying results.
 */
const OverallSearchPopup = (props) => {
  const { anchor, setShowPopup, popupClass, insertTaskChip } = props;
  const [searchText, setSearchText] = useState("");
  const findInputRef = useRef(null);
  const [results, setResults] = useState([]);
  const [noResultFound, setNoResultFound] = useState(false);
  const [entityName, setEntityName] = useState(props.entityName ? props.entityName : null);
  const [projectId, setProjectId] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const { lastSelectedBoard } = useSelector((state) => state.auth.user);
  const { fileMetaData, selectedContentManagerItem, contentManagerData, stateContentManagerData } = useSelector((state) => state.contentManager);
  const location = useLocation();
  const pathname = location.pathname;
  const routeParams = useParams();
  const fileId = useMemo(() => resolveCmsFileIdFromLocation(pathname, routeParams), [pathname, routeParams?.fileId]);
  const isCmsLibraryOrFileRoute = Boolean(
    pathname?.includes(route.PRIVATE_ROUTE.CMS.PATH) || pathname?.includes(route.PRIVATE_ROUTE.Y_DOC.PATH)
  );
  const isCmsDoxDocumentOpen = Boolean(fileMetaData?.item?.mimeType === label.DOX_MIME_TYPE);
  
  const hasActiveEditor = Boolean(getActiveEditorView(fileId));
  const showCmsDocumentSearchUi = Boolean(isCmsLibraryOrFileRoute && isCmsDoxDocumentOpen && hasActiveEditor);
  const [isDocumentTab, setIsDocumentTab] = useState(showCmsDocumentSearchUi);
  const isDocumentSearchMode = Boolean(showCmsDocumentSearchUi && isDocumentTab && hasActiveEditor);
  const resolveEditorView = useCallback(() => getActiveEditorView(fileId), [fileId]);

  const {
    searchTerm: findTerm,
    setSearchTerm: setFindTerm,
    matches: findMatches,
    currentIndex: findCurrentIndex,
    searchOptions,
    throttledSearch: throttledDocSearch,
    navigateMatch,
    clearFind,
  } = useFindInDocument({
    resolveView: resolveEditorView,
    enabled: isDocumentSearchMode,
    revalidateWhen: isDocumentSearchMode,
  });

  const { inEditMode } = useSelector((state) => state.allProjects);
  const globalContextSearch = useSelector((state) => state?.allProjects?.contextSearch ?? "");
  const [contextSearch, setContextSearch] = useState(false);
  const [cursor, setCursor] = useState({ parentIdx: 0, childIdx: 0 });
  const [selected, setSelected] = useState(null);
  const downPress = useKeyPress("ArrowDown");
  const upPress = useKeyPress("ArrowUp");
  const enterPress = useKeyPress("Enter");
  const element = document.querySelector(".active-search-item");
  const excludeList = useSelector((state) => state?.linkedTasks?.excludeLinkTask);
  const searchInputRef = useRef(null);
  const controllerRef = useRef(null);
  const [firstButtonText, setFirstButtonText] = useState("");
  const [isFirstButtonActive, setIsFirstButtonActive] = useState(true);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const searchPopupRef = React.useRef();
  const { teamLeftSelectedTab, userLeftSelectedTab } = useSelector((state) => state.projectSidebar);
  const activeOrganizationSidebar = useSelector((state) => state.admin.activeOrganizationSidebar);
  const isOnLandingPage = pathname === route.PRIVATE_ROUTE.LANDING_PAGE.PATH;
  /**
   * Determines if the search icon should be shown based on the current route and tab.
   */
  const shouldShowIcon = useMemo(() => {
    if (isOnLandingPage) return false;
    if (hasActiveEditor) return true;
    if (SIMPLE_SEARCH_PATHS.has(pathname)) return true;
    if (pathname?.includes(route.PRIVATE_ROUTE.CMS.PATH) || pathname?.includes(route.PRIVATE_ROUTE.Y_DOC.PATH)) return true;
    if (pathname?.includes(route.PRIVATE_ROUTE.AUTOMATION_DETAIL.PATH)) return true;
    if (lastSelectedBoard === label.STREAM && pathname !== route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH && !inEditMode) return true;
    if (pathname.includes(route.PRIVATE_ROUTE.TEAM_SETTING.PATH) && SETTING_TABS.has(teamLeftSelectedTab)) return true;
    if (
      pathname.includes(route.PRIVATE_ROUTE.USER_PROFILE_SETTING.PATH) &&
      (SETTING_TABS.has(userLeftSelectedTab) || userLeftSelectedTab === `${user?.operationalTeamName}s`)
    )
      return true;
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && ADMIN_ORG_FEATURES.has(activeOrganizationSidebar)) return true;
    if (pathname?.includes(route.ADMIN_ROUTE.ACTIVITY_LOG.PATH)) return true;

    return false;
  }, [hasActiveEditor, pathname, lastSelectedBoard, teamLeftSelectedTab, userLeftSelectedTab, inEditMode, activeOrganizationSidebar, isOnLandingPage, user?.operationalTeamName]);

  /**
   * Sets contextSearch state based on shouldShowIcon and entityName prop.
   */
  useEffect(() => {
    setContextSearch(shouldShowIcon && props.entityName !== label.LINKED_TASK && props.entityName !== editorEntity.TASK_DESC);
  }, [shouldShowIcon, props.entityName]);

  /** Default to Document tab when CMS DOX + editor context is active; Task when not. */
  useEffect(() => {
    setIsDocumentTab(Boolean(showCmsDocumentSearchUi));
  }, [showCmsDocumentSearchUi]);

  /**
   * Sets the first button text based on the current pathname and selected board/tab.
   */
  useEffect(() => {
    const buttonText = getSearchButtonText({
      pathname,
      lastSelectedBoard,
      teamLeftSelectedTab,
      userLeftSelectedTab,
      inEditMode,
      activeOrganizationSidebar,
      operationalTeamName: user?.operationalTeamName,
    });
    setFirstButtonText(buttonText);
  }, [pathname, lastSelectedBoard, teamLeftSelectedTab, userLeftSelectedTab, inEditMode, activeOrganizationSidebar, user?.operationalTeamName]);

  /**
   * Determines if the recent search should use the Redux context search.
   * @author Bhavana
   */
  const useReduxContextForRecent = useMemo(() => contextSearch && !(isFirstButtonActive && firstButtonText === label.LIBRARY),
    [contextSearch, isFirstButtonActive, firstButtonText]);

  /**
   * Sets the search text to the global context search when context search is active.
   * @author Bhavana
   */
  useEffect(() => {
    contextSearch && setSearchText(globalContextSearch);
  }, [contextSearch, globalContextSearch]);

  /**
   * Scrolls the window slightly when results change.
   */
  useEffect(() => window.scroll(number.ZERO, number.FIVE), [results]);

  /**
   * Scrolls the active search item into view on up/down arrow press.
   */
  useEffect(() => {
    element && element.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
  }, [upPress, downPress]);

  /**
   * Handles cursor movement on down arrow press.
   */
  useEffect(() => {
    if (results.length && downPress) {
      setCursor((prevState) => {
        return handleArrowDown(prevState, results);
      });
    }
  }, [downPress]);

  /**
   * Handles cursor movement on up arrow press.
   */
  useEffect(() => {
    if (results.length && upPress) {
      setCursor((prevState) => {
        return handleArrowUp(prevState, results);
      });
    }
  }, [upPress]);

  /**
   * Handles selection of a search result on enter press.
   */
  useEffect(() => {
    if (results.length && enterPress) {
      setSelected(results[cursor.parentIdx]?.searchResult[cursor.childIdx]);
    }
  }, [cursor, enterPress]);

  /**
   * Gets entity name and project ID from the current route.
   */
  useEffect(() => {
    getEntityNameAndProjectId();
  }, [pathname]);

  /**
   * Gets entityName and projectId from the route.
   */
  const getEntityNameAndProjectId = () => {
    const entityRoute = [...Object.values(route.PRIVATE_ROUTE), ...Object.values(route.SUPER_ADMIN_ROUTE), ...Object.values(route.ADMIN_ROUTE)].find(
      (d) => matchPath(pathname, { path: d.ROUTER_PATH, exact: true, strict: false })
    );
    /** Prioritize props.entityName if provided (for task sidebar search) */
    if (props.entityName) {
      setEntityName(props.entityName);
    } else if (entityRoute.hasOwnProperty("ENTITY_NAME")) {
      setEntityName(entityRoute["ENTITY_NAME"]);
    } else {
      setEntityName(null);
    }

    const { params } = matchPath(pathname, { path: entityRoute.ROUTER_PATH, exact: true, strict: false });
    params?.projectId ? setProjectId(params?.projectId) : setProjectId(null);
  };

  /**
   * Debounces the API call for searching based on input value.
   */
  const debouncedSearch = useCallback(
    _.debounce(async (nextValue) => {
      if (nextValue.length > number.ONE) {
        setShowLoader(true);
        if (controllerRef.current) {
          controllerRef.current.abort();
        }
        controllerRef.current = new AbortController();
        const result = await dispatch(
          getSearchResult(
            {
              userId: user.id,
              companyId: user.companyId,
              searchText: nextValue,
              entityName,
              entityId: projectId,
              excludedTasks: props.entityName === "Linked Task" ? excludeList : null,
            },
            controllerRef.current?.signal
          )
        );

        if (result !== errorCode.CANCEL_REQUEST) {
          setShowLoader(false);
          const resultNotFound = result?.some((val) => val.searchResult?.length > number.ZERO);
          setNoResultFound(!resultNotFound);
          setResults(result);
        }
      } else {
        setNoResultFound(false);
        setResults([]);
      }
    }, number.FIVE_HUNDRED),
    [entityName, projectId]
  );

  /**
   * Handles the change in the search input for CMS.
   */
  const handleSearchChange = (event) => {
    if (!contentManagerData.length) return;
    let eventPath = "";
    if (selectedContentManagerItem?.items) {
      const curPath = getFolderPath(selectedContentManagerItem.items[0].path);
      eventPath = `${curPath}/${event.value}`;
    } else {
      const curPath = getFolderPath(contentManagerData[0].path);
      eventPath = `${curPath}/${event.value}`;
    }
    dispatch(
      setStateContentManagerData({
        ...stateContentManagerData,
        filter: {
          logic: "and",
          filters: [{ field: "path", operator: "contains", value: eventPath }],
        },
      })
    );
  };

  /**
   * Handles text input change for search.
   */
  const handleOnChange = (e) => {
    const nextValue = e.target.value;
    if (!checkForWhitespace(nextValue)) return;
    setSearchText(nextValue);
    if (!contextSearch) {
      if (searchText.trim() !== nextValue.trim()) debouncedSearch(nextValue.trim());
      dispatch(setContextSearchText(""));
    } else if (isFirstButtonActive && firstButtonText === label.LIBRARY) {
      handleSearchChange(e);
    } else {
      dispatch(setContextSearchText(e.target.value));
    }
  };

  /**
   * Applies the context search from the recent search.
   * @author Bhavana
   */
  const applyContextSearchFromRecent = useCallback((text) => dispatch(setContextSearchText(text)), []);

  /**
   * Determines if the context search clear button should be shown.
   * @author Bhavana
   */
  const showContextSearchClear = useMemo(() => useReduxContextForRecent && (Boolean(searchText.trim()) || Boolean(String(globalContextSearch ?? "").trim())), [useReduxContextForRecent, searchText, globalContextSearch]);

  /**
   * Clears the context search.
   * @author Bhavana
   */
  const handleClearContextSearch = useCallback(() => {
    dispatch(setContextSearchText(""));
    setSearchText("");
    searchInputRef.current?.focus();
  }, [dispatch]);

  // ---- Document (Find in Document) — shared useFindInDocument hook ----

  const handleFindTermChange = (e) => {
    const val = e.target.value;
    if (!val) {
      clearFind();
      return;
    }
    setFindTerm(val);
    throttledDocSearch(val, searchOptions);
  };

  const handleClearFindTerm = () => {
    clearFind();
    findInputRef.current?.focus();
  };

  const arrowsDisabled = !findTerm || !findMatches.length;

  // ---- End Document search logic ----

  /**
   * Returns recent searches or a message if not enough characters.
   */
  const getRecenetSearches = () => {
    return (
      <RecentSearch
        setSearchText={setSearchText}
        searchInputRef={searchInputRef}
        debouncedSearch={debouncedSearch}
        entityName={entityName}
        insertTaskChip={insertTaskChip}
        useReduxContextSearch={useReduxContextForRecent}
        onContextSearchPick={applyContextSearchFromRecent}
      />
    );
  };

  /**
   * Returns search results or a message if no results found.
   */
  const getResults = () => {
    if (noResultFound) {
      return (
        <>
          <div className='no-result'>{quote.NO_RESULT_FOUND}</div>
        </>
      );
    } else {
      return searchText.length < number.TWO ? (
        getRecenetSearches()
      ) : (
        <OverallSearchItems
          results={results}
          setShowPopup={setShowPopup}
          entityName={entityName}
          selected={selected}
          setSelected={setSelected}
          cursor={cursor}
          searchText={searchText}
          insertTaskChip={insertTaskChip}
        />
      );
    }
  };

  /**
   * Handles closing the popup when clicking outside.
   */
  useCloseOnClickOutsideLocal(searchPopupRef, "Search", setShowPopup);

  /**
   * Toggles the first button (context search/library search).
   */
  const toggleFirstButton = () => {
    setIsFirstButtonActive(!isFirstButtonActive);
    dispatch(setContextSearchText(""));
    setContextSearch(!contextSearch);
  };

  const toggleDocumentTab = () => {
    setIsDocumentTab(false);
    dispatch(setContextSearchText(""));
    setContextSearch(!contextSearch);
  };

  /**
   * Determines the placeholder text based on current route and context.
   */
  const placeholderText = useMemo(() => {
    if (pathname === route.ADMIN_ROUTE.MANAGE_USERS.PATH && isFirstButtonActive) {
      return quote.FIND_BY_NAME_OR_EMAIL;
    }
    if (pathname === route.PRIVATE_ROUTE.QUEUES.PATH && isFirstButtonActive) {
      return label.FIND_WORKSPACE;
    }
    if (pathname.includes(route.PRIVATE_ROUTE.USER_PROFILE_SETTING.PATH) && userLeftSelectedTab === `${user?.operationalTeamName}s` && isFirstButtonActive) {
      return label.FIND_IN + " " + (user?.operationalTeamName ? user?.operationalTeamName : label.TEAM) + "...";
    }
    if (pathname.includes(route.PRIVATE_ROUTE.USER_PROFILE_SETTING.PATH) && userLeftSelectedTab === "Views" && isFirstButtonActive) {
      return label.FIND_IN_VIEWS;
    }
    if (pathname.includes(route.PRIVATE_ROUTE.USER_PROFILE_SETTING.PATH) && userLeftSelectedTab === "Workflows" && isFirstButtonActive) {
      return label.FIND_IN_WORKFLOWS;
    }
    if (pathname.includes(route.PRIVATE_ROUTE.USER_PROFILE_SETTING.PATH) && userLeftSelectedTab === "Forms" && isFirstButtonActive) {
      return label.FIND_IN_FORMS;
    }
    if (pathname.includes(route.PRIVATE_ROUTE.USER_PROFILE_SETTING.PATH) && userLeftSelectedTab === "Automation" && isFirstButtonActive) {
      return label.FIND_IN_AUTOMATIONS;
    }
    if (pathname.includes(route.PRIVATE_ROUTE.TEAM_SETTING.PATH) && teamLeftSelectedTab === "Views" && isFirstButtonActive) {
      return label.FIND_IN_VIEWS;
    }
    if (pathname.includes(route.PRIVATE_ROUTE.TEAM_SETTING.PATH) && teamLeftSelectedTab === "Forms" && isFirstButtonActive) {
      return label.FIND_IN_FORMS;
    }
    if (pathname.includes(route.PRIVATE_ROUTE.TEAM_SETTING.PATH) && teamLeftSelectedTab === "Workflows" && isFirstButtonActive) {
      return label.FIND_IN_WORKFLOWS;
    }
    if (pathname.includes(route.PRIVATE_ROUTE.TEAM_SETTING.PATH) && teamLeftSelectedTab === "Members" && isFirstButtonActive) {
      return label.FIND_IN_MEMBERS;
    }
    if (pathname.includes(route.PRIVATE_ROUTE.TEAM_SETTING.PATH) && teamLeftSelectedTab === "Automation" && isFirstButtonActive) {
      return label.FIND_IN_AUTOMATIONS;
    }
    if (pathname === route.PRIVATE_ROUTE.REMINDER.PATH && isFirstButtonActive) {
      return label.FIND_IN_REMINDERS;
    }
    if (pathname === route.PRIVATE_ROUTE.NOTIFICATION.PATH && isFirstButtonActive) {
      return label.FIND_IN_NOTIFICATIONS;
    }
    if (pathname === route.PRIVATE_ROUTE.COMMENT.PATH && isFirstButtonActive) {
      return label.FIND_IN_COMMENTS;
    }
    if (pathname?.includes(route.PRIVATE_ROUTE.CMS.PATH) && isFirstButtonActive) {
      const { params } = matchPath(pathname, {
        path: [route.PRIVATE_ROUTE.CMS.ROUTER_PATH],
      }) || { params: {} };

      const currentPath = selectedContentManagerItem?.path || "";

      if (params?.projectId && params?.projectName) {
        return `Find in ${params.projectName}...`;
      }

      if (currentPath && currentPath.split("/").length >= 2) {
        const pathParts = currentPath.split("/");
        if (pathParts.length >= 2) {
          const projectName = pathParts[1];
          if (projectName && projectName !== "My Library") {
            return `Find in ${projectName}...`;
          }
        }
      }

      return label.FIND_IN_LIBRARY;
    }
    if (pathname === route.PRIVATE_ROUTE.USER_DIRECTORY.PATH && isFirstButtonActive) {
      return label.FIND_IN_USER_DIRECTORY;
    }
    if (pathname?.includes(route.PRIVATE_ROUTE.AUTOMATION_DETAIL.PATH) && isFirstButtonActive) {
      return label.FIND_IN_AUTOMATIONS;
    }
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.JOB_ROLE && isFirstButtonActive) {
      return label.FIND_IN + " " + label.JOB_ROLES + "...";
    }
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.TAGS && isFirstButtonActive) {
      return label.FIND_IN + " " + label.TAGS + "...";
    }
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.BUSINESS_UNITS && isFirstButtonActive) {
      return label.FIND_IN + " " + (FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.BUSINESS_UNITS] || ORGANIZATION_FEATURES_TITLES.BUSINESS_UNITS) + "...";
    }
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.COST_CENTER && isFirstButtonActive) {
      return label.FIND_IN + " " + (FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.COST_CENTER] || ORGANIZATION_FEATURES_TITLES.COST_CENTER) + "...";
    }
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.DEPARTMENTS && isFirstButtonActive) {
      return label.FIND_IN + " " + (FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.DEPARTMENTS] || ORGANIZATION_FEATURES_TITLES.DEPARTMENTS) + "...";
    }
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.OFFICE_LOCATION && isFirstButtonActive) {
      return label.FIND_IN + " " + (FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.OFFICE_LOCATION] || ORGANIZATION_FEATURES_TITLES.OFFICE_LOCATION) + "...";
    }
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.USER_GROUPS && isFirstButtonActive) {
      return label.FIND_IN + " " + (label.USER_GROUPS || ORGANIZATION_FEATURES_TITLES.USER_GROUPS) + "...";
    }
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.CUSTOM_FIELD_SETTINGS && isFirstButtonActive) {
      return label.FIND_IN + " " + (FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.CUSTOM_FIELD_SETTINGS] || ORGANIZATION_FEATURES_TITLES.CUSTOM_FIELD_SETTINGS) + "...";
    }
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.API_INTEGRATION && isFirstButtonActive) {
      return label.FIND_IN + " " + (label.API) + "...";
    }
    if (pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH) && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.TIME_SHEETS && isFirstButtonActive) {
      return label.FIND_IN + " " + (FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.TIME_SHEETS] || ORGANIZATION_FEATURES_TITLES.TIME_SHEETS) + "...";
    }
    if (pathname?.includes(route.ADMIN_ROUTE.ACTIVITY_LOG.PATH) && isFirstButtonActive) {
      return label.FIND_IN + " " + label.ACTIVITY_LOGS + "...";
    }
    return label.FIND;
  }, [pathname, userLeftSelectedTab, isFirstButtonActive, teamLeftSelectedTab, selectedContentManagerItem, activeOrganizationSidebar, user?.operationalTeamName]);

  return (
    <>
      <Popup
        className={entityName === label.LINKED_TASK || entityName === editorEntity.TASK_DESC ? "link-task-search-popup overall-search-wrapper" : ""}
        show={true}
        anchor={anchor.current}
        animate={false}
        collision={{
          horizontal: "fit",
          vertical: "flip",
        }}
        popupClass={"dt-popup"}
        id='dt-recent-search-list'>
        <div
          className={
            popupClass + " dt-popup-body p-3 overflow-hidden d-block border-0 overall-search-popup overall-search-inner align-items-center"
          }
          ref={searchPopupRef}>
          {entityName === editorEntity.TASK_DESC ? (
            <div className='d-flex justify-content-between p-3'>
              <div className='font-weight-bold'>{label.SEARCH_TASK}</div>
              <div className='small'>{label.ESC_PRESS}</div>
            </div>
          ) : (
            <></>
          )}
          {showCmsDocumentSearchUi ? (
            <div className='d-flex  align-items-center justify-content-between'>
              <span className='font-weight-bold search-task-text'>{label.SEARCH}</span>
              <div className='toggle-switch-btn'>
                <button onClick={() => setIsDocumentTab(true)} className={`${isDocumentTab ? "active" : "de-activate"} switch-btn`}>
                  {label.DOCUMENT}
                </button>
                <button onClick={toggleDocumentTab} className={`${!isDocumentTab ? "active" : "de-activate"} switch-btn`}>
                  {tooltip.TASK}
                </button>
              </div>
            </div>
          ) : (
            shouldShowIcon && props.entityName !== label.LINKED_TASK && props.entityName !== editorEntity.TASK_DESC && (
              <div className='d-flex  align-items-center justify-content-between'>
                <span className='font-weight-bold search-task-text'>{label.SEARCH}</span>
                <div className='toggle-switch-btn'>
                  <button onClick={toggleFirstButton} className={`${isFirstButtonActive ? "active" : "de-activate"} switch-btn`}>
                    {firstButtonText}
                  </button>
                  <button onClick={toggleFirstButton} className={`${isFirstButtonActive ? "de-activate " : "active"} switch-btn`}>
                    {tooltip.TASK}
                  </button>
                </div>
              </div>
            )
          )}

          <div className={`overall-search-input-container position-relative d-flex ${isDocumentSearchMode ? "align-items-center" : ""}`}>
            <Input
              type='text'
              autoFocus
              autoComplete='off'
              className='search-input'
              placeholder={isDocumentSearchMode ? label.FIND_IN_DOCUMENT : placeholderText}
              value={isDocumentSearchMode ? findTerm : searchText}
              onChange={isDocumentSearchMode ? handleFindTermChange : handleOnChange}
              ref={isDocumentSearchMode ? findInputRef : searchInputRef}
            />
            {isDocumentSearchMode && findTerm && (
              <span className='position-absolute find-doc-count' style={{ right: "2.5rem", fontSize: "0.75rem", color: "#6c757d", pointerEvents: "none" }}>
                {findMatches.length
                  ? `${findCurrentIndex < 0 ? 0 : findCurrentIndex + 1} of ${findMatches.length}`
                  : `0 of 0`}
              </span>
            )}
            <span className='search-popup-icon position-absolute'>{icon.SEARCH}</span>
            {showContextSearchClear && (
              <span className='search-clear-icon position-absolute' onClick={handleClearContextSearch}>
                {icon.CLEAR}
              </span>
            )}
          </div>

          {isDocumentSearchMode ? (
            <div className='d-flex align-items-center justify-content-end mt-0'>
              <button
                className='find-doc-button'
                disabled={arrowsDisabled}
                title='Previous match'
                onClick={() => navigateMatch("prev")}>
                {icon.CHEVRON_UP}
              </button>
              <button
                className='find-doc-button mr-2'
                disabled={arrowsDisabled}
                title='Next match'
                onClick={() => navigateMatch("next")}>
                {icon.CHEVRON_DOWN}
              </button>
              {findTerm && (
                <span className='cursor-pointer blue text-decoration-underline' onClick={handleClearFindTerm}>
                  {label.CLEAR}
                </span>
              )}
            </div>
          ) : (
            <>
              {!contextSearch && <div className='overall-search-result-container overflow-y-auto'>{getResults()}</div>}
              {showLoader && (
                <div className='text-center mb-2'>
                  <Loader />
                </div>
              )}
            </>
          )}
        </div>
      </Popup>
    </>
  );
};

export default OverallSearchPopup;